import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRight, Github, Linkedin, Mail } from "lucide-react";
import { container, EASE_OUT_EXPO } from "./editorial";
import { SITE } from "../lib/site";
import { prefersLessData, useWhenIdle } from "../lib/useWhenIdle";
import { cn } from "../lib/utils";

const EmbeddingScene = lazy(() => import("./EmbeddingScene"));

const LINES = ["I ship AI products.", "I teach PMs to ship AI.", "I turn data into decisions."];
const SOCIALS = [
  { label: "LinkedIn", href: SITE.linkedin, icon: Linkedin, external: true },
  { label: "GitHub", href: SITE.github, icon: Github, external: true },
  { label: "Email", href: `mailto:${SITE.email}`, icon: Mail, external: false },
];

// One line at a time, held long enough to read, with a soft cross-fade between.
// Pauses while the hero is off-screen; stays on the first line for reduced motion.
function RotatingLine() {
  const reduceMotion = useReducedMotion();
  const ref = useRef<HTMLParagraphElement>(null);
  const inView = useInView(ref);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduceMotion || !inView) return;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % LINES.length), 6000);
    return () => window.clearInterval(timer);
  }, [reduceMotion, inView]);

  return (
    <p ref={ref} className="mt-5 min-h-[1.3em] text-[clamp(1.25rem,2.4vw,1.95rem)] font-medium leading-tight tracking-[-0.025em] text-fg">
      <span className="sr-only">{LINES.join(" ")}</span>
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={LINES[index]}
          aria-hidden="true"
          className="inline-block"
          initial={{ opacity: 0, y: 8, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.9, ease: EASE_OUT_EXPO } }}
          exit={{ opacity: 0, y: -6, filter: "blur(6px)", transition: { duration: 0.5, ease: "easeIn" } }}
        >
          {LINES[index]}
        </motion.span>
      </AnimatePresence>
    </p>
  );
}

function MetricStrip() {
  const ref = useRef<HTMLUListElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const ease = "transition-[stroke-dashoffset,width] [transition-duration:1800ms] ease-out-expo motion-reduce:transition-none";

  return (
    <ul ref={ref} aria-label="Selected impact" className="relative z-[2] mt-4 grid gap-3 md:grid-cols-3">
      <li className="glass flex min-h-[100px] items-center gap-5 rounded-[18px] border border-edge px-5 py-4">
        <svg width="110" height="62" viewBox="0 0 120 66" aria-hidden="true" className="shrink-0 overflow-visible">
          <path d="M10 60 A50 50 0 0 1 110 60" fill="none" stroke="rgb(var(--edge))" strokeWidth="6" strokeLinecap="round" />
          <path
            d="M10 60 A50 50 0 0 1 110 60"
            fill="none"
            stroke="rgb(var(--azure))"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray="157"
            strokeDashoffset={inView ? 51.8 : 157}
            className={cn(ease, "[transition-delay:300ms]")}
          />
        </svg>
        <div>
          <p className="text-[28px] font-semibold leading-none tracking-[-0.04em] tabular-nums">
            61 <span className="font-normal text-fg-subtle">→</span> 67
          </p>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.08em] text-fg-subtle">Passenger NPS · Snapp!</p>
        </div>
      </li>
      <li className="glass flex min-h-[100px] items-center gap-5 rounded-[18px] border border-edge px-5 py-4">
        <div aria-hidden="true" className="grid w-[104px] shrink-0 gap-[7px]">
          <span className="h-1.5 rounded-full bg-edge-strong" />
          <span className={cn("h-1.5 rounded-full bg-mint [transition-delay:500ms]", ease)} style={{ width: inView ? "83%" : "100%" }} />
        </div>
        <div>
          <p className="text-[28px] font-semibold leading-none tracking-[-0.04em] tabular-nums">
            4.2 <span className="font-normal text-fg-subtle">→</span> 3.5
          </p>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.08em] text-fg-subtle">Min. pickup wait · Snapp!</p>
        </div>
      </li>
      <li className="glass flex min-h-[100px] items-center gap-5 rounded-[18px] border border-edge px-5 py-4">
        <svg width="104" height="52" viewBox="0 0 110 56" aria-hidden="true" className="shrink-0 overflow-visible">
          <polyline
            points="2,52 18,49 32,46 46,40 60,34 72,25 86,16 100,8 108,4"
            fill="none"
            stroke="rgb(var(--mint))"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="260"
            strokeDashoffset={inView ? 0 : 260}
            className={cn(ease, "[transition-delay:400ms]")}
          />
        </svg>
        <div>
          <p className="text-[28px] font-semibold leading-none tracking-[-0.04em] tabular-nums">
            70K <span className="font-normal text-fg-subtle">DAU</span>
          </p>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.08em] text-fg-subtle">In under a year · Produpt</p>
        </div>
      </li>
    </ul>
  );
}

// The entrance is a CSS animation, not framer-motion: it starts on first paint,
// runs off the main thread and doesn't hold back the largest contentful paint.
const rise = (delayMs: number) => ({ style: { animationDelay: `${delayMs}ms` } });

export default function HeroSection() {
  const stageRef = useRef<HTMLDivElement>(null);
  // three.js waits until the page has painted and settled; the map then fades in.
  const sceneReady = useWhenIdle() && !prefersLessData();

  return (
    <section id="hero" className="relative isolate overflow-hidden">
      {sceneReady && (
        <Suspense fallback={null}>
          <EmbeddingScene anchorRef={stageRef} />
        </Suspense>
      )}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(90deg,rgb(var(--night))_0%,rgb(var(--night)/0.7)_32%,transparent_62%)] max-lg:bg-[linear-gradient(180deg,rgb(var(--night)/0.85)_0%,rgb(var(--night)/0.4)_45%,transparent_70%)]"
      />

      <div className={cn(container, "relative z-[2] pb-12 pt-24 lg:pb-14 lg:pt-28")}>
        <div className="grid items-center gap-6 lg:min-h-[calc(100svh-15rem)] lg:grid-cols-[1.05fr_1fr] lg:gap-10">
          {/* The cluster labels are laid out to stay clear of this block (see layout.ts). */}
          <div data-hero-copy>
            <p
              {...rise(0)}
              className="glass motion-safe:animate-rise inline-flex flex-wrap items-center gap-2.5 rounded-full border border-edge py-2 pl-3 pr-3.5 font-mono text-xs uppercase tracking-[0.08em] text-fg-muted"
            >
              <span aria-hidden="true" className="h-[7px] w-[7px] rounded-full bg-azure" />
              <span className="whitespace-nowrap">
                Senior <span className="sm:hidden">PM</span>
                <span className="hidden sm:inline">Product Manager</span> · AI &amp; ML · Berlin
              </span>
            </p>

            <h1
              {...rise(60)}
              className="motion-safe:animate-rise mt-6 text-[clamp(3.2rem,7.2vw,6.6rem)] font-semibold leading-[0.95] tracking-[-0.055em] text-fg"
            >
              Hey, I'm{" "}
              <em className="font-serif text-[1.12em] font-normal italic tracking-[-0.02em] text-azure-text">Danial</em>.
            </h1>

            <div {...rise(120)} className="motion-safe:animate-rise">
              <RotatingLine />
            </div>

            <div {...rise(180)} className="motion-safe:animate-rise mt-9 flex flex-wrap items-center gap-3">
              <a
                href="#contact"
                className="group inline-flex h-[50px] items-center gap-2.5 rounded-full bg-azure px-6 text-[15px] font-medium text-night shadow-[0_10px_30px_-12px_rgb(var(--azure))] transition-transform duration-300 ease-out-expo hover:-translate-y-0.5"
              >
                Let's talk
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
              <a
                href="#ask"
                className="glass inline-flex h-[50px] items-center rounded-full border border-edge-strong px-6 text-[15px] font-medium text-fg transition-transform duration-300 ease-out-expo hover:-translate-y-0.5"
              >
                Ask me anything
              </a>
              <ul className="flex gap-1 sm:ml-2">
                {SOCIALS.map(({ label, href, icon: Icon, external }) => (
                  <li key={label}>
                    <a
                      href={href}
                      aria-label={label}
                      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-full text-fg-subtle transition-colors hover:bg-night-3 hover:text-fg"
                    >
                      <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div ref={stageRef} className="relative h-[clamp(380px,62vw,460px)] lg:h-[min(72vh,660px)]">
            <div
              aria-hidden="true"
              className="absolute bottom-0 left-1/2 aspect-square w-[90%] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgb(var(--azure)/0.13),transparent)]"
            />
            {/* Centred by this wrapper; the entrance animation owns the inner element's transform. */}
            <div className="absolute bottom-0 left-1/2 z-[2] w-[min(100%,590px)] -translate-x-1/2">
              <picture className="block motion-safe:animate-rise" style={{ animationDelay: "180ms", animationDuration: "1.1s" }}>
                <source
                  type="image/webp"
                  srcSet="/danial-cutout-480.webp 480w, /danial-cutout-640.webp 640w, /danial-cutout-720.webp 720w, /danial-cutout-820.webp 820w, /danial-cutout.webp 1100w"
                  sizes="(min-width: 670px) 590px, calc(100vw - 40px)"
                />
                <img
                  src="/danial-cutout.png"
                  alt="Portrait of Danial Darabi"
                  width={1100}
                  height={975}
                  decoding="async"
                  className="mask-fade-b pointer-events-none h-auto w-full select-none"
                />
              </picture>
            </div>
            <p className="absolute right-0 top-1 z-[3] hidden font-mono text-[11px] uppercase tracking-[0.08em] text-fg-subtle lg:block">
              Hover the clusters
            </p>
          </div>
        </div>

        <MetricStrip />
      </div>
    </section>
  );
}
